/**
 * Independent time operation tool to facilitate subsequent switch to dayjs
 */
import dayjs, { Dayjs } from 'dayjs';
import { isString } from './is';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const DATE_FORMAT = 'YYYY-MM-DD';

export function formatToDateTime(
  date: string | number | dayjs.Dayjs | Date | null | undefined = undefined,
): string {
  if (!date && date !== 0) {
    return '';
  }
  if (isString(date) && !/\D/.test(date)) {
    date = Number(date);
  }
  return dayjs(date).format(DATE_TIME_FORMAT);
}

export function formatToDate(
  date: string | number | dayjs.Dayjs | Date | null | undefined = undefined,
): string {
  if (!date && date !== 0) {
    return '';
  }
  if (isString(date) && !/\D/.test(date)) {
    date = Number(date);
  }
  return dayjs(date).format(DATE_FORMAT);
}

export const dateUtil = dayjs;

// 判断指定日期是不是今天
export const checkToday = (current: Dayjs) => {
  if (!current) return false;
  const now = dayjs().endOf('day');
  return now.isSame(current, 'day');
};

// 日期组件-只能选择n天内时间
export const disabledRangeDate = (n: number, date: Dayjs) => {
  const now = dayjs().endOf('day');
  if (checkToday(date)) return false;
  if (n >= 0) {
    return (date && date < now) || date.diff(now, 'day') > n - 1;
  } else {
    return (date && date > now) || date.diff(now, 'day') <= n - 1;
  }
};

// 日期组件-只能选择当前时间之前的时刻
export const disabledAfterDate = (date: Dayjs) => {
  if (!date) return false;
  return !dayjs().isSameOrAfter(date, 'day');
};
// 只能选择当前时间之后的时刻
export const disabledBeforeDate = (date: Dayjs) => {
  if (!date) return false;
  return !dayjs().isSameOrBefore(date, 'day');
};

// 日期组件-显示时间
const range = (start: number, end: number) => {
  const result: number[] = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};
// 禁用当前时间段之后的时间
export const disabledAfterTime = (current?: Dayjs) => {
  current = current || dayjs();
  const h = current.hour();
  const m = current.minute();
  const s = current.second();
  return {
    disabledHours: () => range(h + 1, 24),
    disabledMinutes: (_h) => {
      if (h === _h) return range(m, 60);
      return [];
    },
    disabledSeconds: (_s) => {
      if (s === _s) return range(s, 60);
      return [];
    },
  };
};
// 禁用当前时间段之前的时间
export const disabledBeforeTime = (current: Dayjs) => {
  const h = current.hour();
  const m = current.minute();
  const s = current.second();
  return {
    disabledHours: () => range(0, h + 1),
    disabledMinutes: (_h) => {
      if (h === _h) return range(0, m);
      return [];
    },
    disabledSeconds: (_s) => {
      if (s === _s) return range(0, s);
      return [];
    },
  };
};

// 将时间戳转为 2000211 这种格式
export const timestampToCustomFormat = (timestamp, isMonth?: boolean, isYears?: boolean) => {
  // 创建一个新的 Date 对象，将时间戳作为参数传入
  const date = new Date(timestamp);

  // 获取年、月、日信息
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // 月份从0开始，需要加1，然后补齐两位
  const day = date.getDate().toString().padStart(2, '0'); // 补齐两位

  // 将年、月、日拼接成指定格式的字符串
  const formattedDate = `${year}${month}${day}`;

  if (isMonth) {
    return `${year}${month}`;
  }
  if (isYears) {
    return `${year}`;
  }
  return formattedDate;
};